import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminVerify, listPhotos, listVideos, createPhoto, createVideo, deletePhoto, deleteVideo, DEFAULT_BASE_URL } from '../../services/api';
import { ArrowLeft, Image, Video as VideoIcon, Save, Trash2, Plus, Upload, Link as LinkIcon, X, CheckCircle, AlertCircle } from 'lucide-react';
// Get base URL for API calls
const getBaseUrl = () => {
  return (DEFAULT_BASE_URL || `${window.location.origin}/api`).replace(/\/$/, '');
};

const emptyPhoto = { title: '', url: '', category: '', event: '', description: '' };
const emptyVideo = { title: '', url: '', category: '', event: '', description: '' };
const MAX_IMAGE_SIZE_BYTES = 1 * 1024 * 1024;

const AdminGalleryPage = () => {
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [tab, setTab] = useState('photos');
  const [photoForm, setPhotoForm] = useState(emptyPhoto);
  const [videoForm, setVideoForm] = useState(emptyVideo);
  const [photoFile, setPhotoFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [photoFiles, setPhotoFiles] = useState([]); // For bulk upload
  const [videoUrls, setVideoUrls] = useState([]); // For bulk video URLs
  const [videoFiles, setVideoFiles] = useState([]); // For bulk video file upload
  const [bulkMode, setBulkMode] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const init = async () => {
      try {
        await adminVerify();
        await refresh();
      } catch (e) {
        navigate('/admin');
      }
    };
    init();
  }, [navigate]);

  async function refresh() {
    setLoading(true);
    setError('');
    try {
      const [p, v] = await Promise.all([listPhotos(), listVideos()]);
      setPhotos(Array.isArray(p) ? p : []);
      setVideos(Array.isArray(v) ? v : []);
    } catch (e) {
      setError(e.message || 'Failed to load gallery');
    } finally {
      setLoading(false);
    }
  }

  const handlePhotoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        setError('Image size must be 1MB or less');
        e.target.value = '';
        return;
      }
      setPhotoFile(file);
      setPhotoForm({ ...photoForm, url: '' }); // Clear URL when file is selected
    }
  };

  const handleVideoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setVideoFile(file);
      setVideoForm({ ...videoForm, url: '' }); // Clear URL when file is selected
    }
  };

  const handleBulkPhotoFilesChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) {
      setPhotoFiles([]);
      return;
    }
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    const sizeValidImageFiles = imageFiles.filter(file => file.size <= MAX_IMAGE_SIZE_BYTES);
    const invalidTypeCount = files.length - imageFiles.length;
    const oversizeCount = imageFiles.length - sizeValidImageFiles.length;
    if (invalidTypeCount > 0 || oversizeCount > 0) {
      const issues = [];
      if (invalidTypeCount > 0) issues.push(`${invalidTypeCount} non-image file(s)`);
      if (oversizeCount > 0) issues.push(`${oversizeCount} file(s) over 1MB`);
      setError(`${issues.join(' and ')} were skipped. Only image files up to 1MB are supported.`);
      setTimeout(() => setError(''), 5000);
    }
    setPhotoFiles(sizeValidImageFiles);
  };

  const handleBulkVideoUrlsChange = (e) => {
    const urls = e.target.value.split('\n').map(url => url.trim()).filter(url => url.length > 0);
    setVideoUrls(urls);
  };

  const handleBulkVideoFilesChange = (e) => {
    const files = Array.from(e.target.files);
    const videoFiles = files.filter(file => file.type.startsWith('video/'));
    if (videoFiles.length !== files.length) {
      setError(`${files.length - videoFiles.length} non-video file(s) were skipped`);
    }
    setVideoFiles(videoFiles);
  };

  async function submitPhoto(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      if (!photoForm.title) {
        throw new Error('Title is required');
      }
      
      if (!photoFile && !photoForm.url) {
        throw new Error('Please provide either a file upload or URL');
      }

      let payload = {
        title: photoForm.title,
        category: photoForm.category || '',
        event: photoForm.event || '',
        description: photoForm.description || ''
      };

      if (photoFile) {
        // File upload - use FormData
        const formData = new FormData();
        formData.append('image', photoFile);
        formData.append('title', photoForm.title);
        if (photoForm.category) formData.append('category', photoForm.category);
        if (photoForm.event) formData.append('event', photoForm.event);
        if (photoForm.description) formData.append('description', photoForm.description);

        const token = localStorage.getItem('ifm_admin_token');
        const res = await fetch(`${getBaseUrl()}/gallery/photos`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.error || errorData.message || 'Failed to upload photo');
        }

        const data = await res.json();
        setSuccess('Photo uploaded successfully!');
      } else {
        // URL input
        payload.url = photoForm.url;
        await createPhoto(payload);
        setSuccess('Photo added successfully!');
      }

      setPhotoForm(emptyPhoto);
      setPhotoFile(null);
      setShowForm(false);
      await refresh();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function submitVideo(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      if (!videoForm.title) {
        throw new Error('Title is required');
      }
      
      if (!videoForm.url) {
        throw new Error('Video URL is required');
      }

      const payload = {
        title: videoForm.title,
        url: videoForm.url,
        category: videoForm.category || '',
        event: videoForm.event || '',
        description: videoForm.description || ''
      };

      await createVideo(payload);
      setSuccess('Video added successfully!');
      setVideoForm(emptyVideo);
      setVideoFile(null);
      setShowForm(false);
      await refresh();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function submitBulkPhotos(e) {
    e.preventDefault();
    if (photoFiles.length === 0) {
      setError('Please select at least one photo file');
      return;
    }

    setBulkUploading(true);
    setError('');
    setSuccess('');
    
    const token = localStorage.getItem('ifm_admin_token');
    let successCount = 0;
    let failCount = 0;
    const errors = [];

    for (let i = 0; i < photoFiles.length; i++) {
      const file = photoFiles[i];
      try {
        const formData = new FormData();
        formData.append('image', file);
        formData.append('title', file.name.replace(/\.[^/.]+$/, '') || `Photo ${i + 1}`);
        if (photoForm.category) formData.append('category', photoForm.category);
        if (photoForm.event) formData.append('event', photoForm.event);
        if (photoForm.description) formData.append('description', photoForm.description);

        const res = await fetch(`${getBaseUrl()}/gallery/photos`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.error || errorData.message || 'Failed to upload');
        }
        successCount++;
      } catch (e) {
        failCount++;
        errors.push(`${file.name}: ${e.message}`);
      }
    }

    if (successCount > 0) {
      setSuccess(`Successfully uploaded ${successCount} photo(s)${failCount > 0 ? `. ${failCount} failed.` : ''}`);
      if (errors.length > 0 && errors.length <= 3) {
        setError(errors.join('; '));
      } else if (errors.length > 3) {
        setError(`${errors.length} files failed. First error: ${errors[0]}`);
      }
      setPhotoFiles([]);
      setPhotoForm(emptyPhoto);
      setShowForm(false);
      await refresh();
      setTimeout(() => {
        setSuccess('');
        setError('');
      }, 5000);
    } else {
      setError(`Failed to upload all photos. ${errors.slice(0, 3).join('; ')}`);
    }
    
    setBulkUploading(false);
  }

  async function submitBulkVideos(e) {
    e.preventDefault();
    if (videoUrls.length === 0) {
      setError('Please provide at least one video URL. Video file uploads are not yet supported.');
      return;
    }

    setBulkUploading(true);
    setError('');
    setSuccess('');
    
    const token = localStorage.getItem('ifm_admin_token');
    let successCount = 0;
    let failCount = 0;
    const errors = [];

    // Handle video file uploads
    // Note: Backend currently only supports video URLs, not file uploads
    // Video file uploads will be skipped with a warning
    if (videoFiles.length > 0) {
      setError('Video file uploads are not yet supported by the backend. Please use video URLs instead. File uploads will be skipped.');
      // Skip video file uploads for now
      failCount += videoFiles.length;
      videoFiles.forEach(file => {
        errors.push(`${file.name}: Video file uploads not supported. Please use URLs instead.`);
      });
    }

    // Handle video URLs
    for (let i = 0; i < videoUrls.length; i++) {
      const url = videoUrls[i];
      try {
        const payload = {
          title: `Video ${videoFiles.length + i + 1}`,
          url: url,
          category: videoForm.category || '',
          event: videoForm.event || '',
          description: videoForm.description || ''
        };

        await createVideo(payload);
        successCount++;
      } catch (e) {
        failCount++;
        errors.push(`${url}: ${e.message}`);
      }
    }

    if (successCount > 0) {
      setSuccess(`Successfully added ${successCount} video(s)${failCount > 0 ? `. ${failCount} failed.` : ''}`);
      if (errors.length > 0 && errors.length <= 3) {
        setError(errors.join('; '));
      } else if (errors.length > 3) {
        setError(`${errors.length} items failed. First error: ${errors[0]}`);
      }
      setVideoUrls([]);
      setVideoFiles([]);
      setVideoForm(emptyVideo);
      setShowForm(false);
      await refresh();
      setTimeout(() => {
        setSuccess('');
        setError('');
      }, 5000);
    } else {
      setError(`Failed to add all videos. ${errors.slice(0, 3).join('; ')}`);
    }
    
    setBulkUploading(false);
  }

  const handleDeletePhoto = async (id) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;
    try {
      await deletePhoto(id);
      setSuccess('Photo deleted successfully!');
      await refresh();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.message || 'Failed to delete photo');
    }
  };

  const handleDeleteVideo = async (id) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;
    try {
      await deleteVideo(id);
      setSuccess('Video deleted successfully!');
      await refresh();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.message || 'Failed to delete video');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/admin')} 
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{'Gallery'}</h1>
              <p className="text-xs text-gray-500">Upload photos and videos or add via URL</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                setTab('photos');
                setBulkMode(false);
                setShowForm(true);
                setPhotoFiles([]);
                setVideoUrls([]);
                setVideoFiles([]);
                setPhotoForm(emptyPhoto);
                setVideoForm(emptyVideo);
                setPhotoFile(null);
                setVideoFile(null);
                setError('');
                // Scroll to form
                setTimeout(() => {
                  document.getElementById('gallery-form-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 100);
              }}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all shadow-md hover:shadow-lg font-semibold text-sm"
            >
              <Plus className="h-5 w-5" />
              {'Add Photo'}
            </button>
            <button
              onClick={() => {
                setTab('videos');
                setBulkMode(false);
                setShowForm(true);
                setPhotoFiles([]);
                setVideoUrls([]);
                setVideoFiles([]);
                setPhotoForm(emptyPhoto);
                setVideoForm(emptyVideo);
                setPhotoFile(null);
                setVideoFile(null);
                setError('');
                // Scroll to form
                setTimeout(() => {
                  document.getElementById('gallery-form-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 100);
              }}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all shadow-md hover:shadow-lg font-semibold text-sm"
            >
              <Plus className="h-5 w-5" />
              {'Add Video'}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Success Message */}
        {success && (
          <div className="mb-6 bg-green-50 border-l-4 border-green-500 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center">
              <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
              <p className="text-sm text-green-700">{success}</p>
            </div>
            <button onClick={() => setSuccess('')} className="text-green-600 hover:text-green-800">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-3" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <button onClick={() => setError('')} className="text-red-600 hover:text-red-800">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Tabs */}
        <div id="gallery-form-section" className="bg-white shadow-md rounded-xl mb-8 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => {
                  setTab('photos');
                  setBulkMode(false);
                  setShowForm(true);
                  setPhotoFiles([]);
                  setVideoUrls([]);
                  setVideoFiles([]);
                  setPhotoForm(emptyPhoto);
                  setVideoForm(emptyVideo);
                  setError('');
                }} 
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  tab === 'photos' 
                    ? 'bg-primary-600 text-white shadow-md' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Image className="inline h-4 w-4 mr-2" /> Photos
              </button>
              <button 
                onClick={() => {
                  setTab('videos');
                  setBulkMode(false);
                  setShowForm(true);
                  setPhotoFiles([]);
                  setVideoUrls([]);
                  setVideoFiles([]);
                  setPhotoForm(emptyPhoto);
                  setVideoForm(emptyVideo);
                  setError('');
                }} 
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  tab === 'videos' 
                    ? 'bg-primary-600 text-white shadow-md' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <VideoIcon className="inline h-4 w-4 mr-2" /> Videos
              </button>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  setBulkMode(!bulkMode);
                  setShowForm(true);
                  setPhotoFiles([]);
                  setVideoUrls([]);
                  setVideoFiles([]);
                  setPhotoForm(emptyPhoto);
                  setVideoForm(emptyVideo);
                  setPhotoFile(null);
                  setVideoFile(null);
                  setError('');
                  // Clear file inputs
                  const photoInput = document.getElementById('bulk-photo-upload');
                  const videoInput = document.getElementById('bulk-video-upload');
                  const singlePhotoInput = document.getElementById('photo-upload');
                  if (photoInput) photoInput.value = '';
                  if (videoInput) videoInput.value = '';
                  if (singlePhotoInput) singlePhotoInput.value = '';
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  bulkMode 
                    ? 'bg-emerald-600 text-white shadow-md' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Upload className="inline h-4 w-4 mr-2" />
                {bulkMode ? 'Single Upload' : 'Bulk Upload'}
              </button>
              {showForm && (
                <button
                  onClick={() => {
                    setShowForm(false);
                    setPhotoFiles([]);
                    setVideoUrls([]);
                    setVideoFiles([]);
                    setPhotoForm(emptyPhoto);
                    setVideoForm(emptyVideo);
                    setPhotoFile(null);
                    setVideoFile(null);
                    setError('');
                  }}
                  className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
                  title="Close Form"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>

          {/* Form Section */}
          {showForm && (
            <div className="p-6">
            {tab === 'photos' ? (
              bulkMode ? (
                <form onSubmit={submitBulkPhotos} className="space-y-6">
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg mb-4">
                    <p className="text-sm text-blue-700">
                      <strong>Bulk Upload Mode:</strong> Select multiple photos at once. All photos will share the same category, event, and description below.
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Select Multiple Photos <span className="text-red-500">*</span>
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-400 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleBulkPhotoFilesChange}
                        className="hidden"
                        id="bulk-photo-upload"
                      />
                      <label
                        htmlFor="bulk-photo-upload"
                        className="cursor-pointer flex flex-col items-center"
                      >
                        <Upload className="h-8 w-8 text-gray-400 mb-2" />
                        <span className="text-sm text-gray-600">
                          {photoFiles.length > 0 
                            ? `${photoFiles.length} file(s) selected` 
                            : 'Click to select multiple images'}
                        </span>
                      </label>
                      {photoFiles.length > 0 && (
                        <div className="mt-4 text-left">
                          <p className="text-xs text-gray-600 mb-2">Selected files:</p>
                          <div className="max-h-32 overflow-y-auto space-y-1">
                            {photoFiles.map((file, idx) => (
                              <div key={idx} className="text-xs text-gray-700 bg-white p-2 rounded border">
                                {file.name} ({(file.size / 1024).toFixed(1)} KB)
                              </div>
                            ))}
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setPhotoFiles([]);
                              document.getElementById('bulk-photo-upload').value = '';
                            }}
                            className="mt-2 text-xs text-red-600 hover:text-red-800"
                          >
                            Clear all files
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Category (applied to all)</label>
                      <input 
                        type="text"
                        className="w-full border-2 border-gray-300 rounded-lg px-4 py-2.5 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all outline-none" 
                        value={photoForm.category} 
                        onChange={e => setPhotoForm({ ...photoForm, category: e.target.value })}
                        placeholder="e.g., Events, Awards"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Event (applied to all)</label>
                      <input 
                        type="text"
                        className="w-full border-2 border-gray-300 rounded-lg px-4 py-2.5 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all outline-none" 
                        value={photoForm.event} 
                        onChange={e => setPhotoForm({ ...photoForm, event: e.target.value })}
                        placeholder="Optional event name"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Description (applied to all)</label>
                      <textarea 
                        className="w-full border-2 border-gray-300 rounded-lg px-4 py-2.5 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all outline-none resize-none"
                        rows="3"
                        value={photoForm.description} 
                        onChange={e => setPhotoForm({ ...photoForm, description: e.target.value })}
                        placeholder="Optional description"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-4 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => {
                        setPhotoFiles([]);
                        setPhotoForm(emptyPhoto);
                        setError('');
                        document.getElementById('bulk-photo-upload').value = '';
                      }}
                      className="px-6 py-2.5 border-2 border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                    >
                      Clear
                    </button>
                    <button 
                      type="submit" 
                      disabled={bulkUploading || photoFiles.length === 0}
                      className="inline-flex items-center gap-2 bg-primary-600 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
                    >
                      <Save className="h-4 w-4" /> 
                      {bulkUploading ? `Uploading...` : `Upload ${photoFiles.length} Photo(s)`}
                    </button>
                  </div>
                </form>
              ) : (
              <form onSubmit={submitPhoto} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input 
                      type="text"
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-2.5 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all outline-none" 
                      value={photoForm.title} 
                      onChange={e => setPhotoForm({ ...photoForm, title: e.target.value })}
                      placeholder="Enter photo title"
                      required
                    />
                  </div>

                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Upload Photo File
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-400 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoFileChange}
                        className="hidden"
                        id="photo-upload"
                      />
                      <label
                        htmlFor="photo-upload"
                        className="cursor-pointer flex flex-col items-center"
                      >
                        <Upload className="h-8 w-8 text-gray-400 mb-2" />
                        <span className="text-sm text-gray-600">
                          {photoFile ? photoFile.name : 'Click to upload image'}
                        </span>
                      </label>
                      {photoFile && (
                        <button
                          type="button"
                          onClick={() => {
                            setPhotoFile(null);
                            document.getElementById('photo-upload').value = '';
                          }}
                          className="mt-2 text-xs text-red-600 hover:text-red-800"
                        >
                          Remove file
                        </button>
                      )}
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      <LinkIcon className="inline h-4 w-4 mr-1" />
                      Or Provide URL
                    </label>
                    <input 
                      type="url"
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-2.5 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all outline-none" 
                      value={photoForm.url} 
                      onChange={e => {
                        setPhotoForm({ ...photoForm, url: e.target.value });
                        if (e.target.value) setPhotoFile(null);
                      }}
                      placeholder="https://example.com/image.jpg"
                      disabled={!!photoFile}
                    />
                    {photoFile && (
                      <p className="text-xs text-gray-500 mt-1">Clear file upload to use URL</p>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Category</label>
                    <input 
                      type="text"
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-2.5 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all outline-none" 
                      value={photoForm.category} 
                      onChange={e => setPhotoForm({ ...photoForm, category: e.target.value })}
                      placeholder="e.g., Events, Awards"
                    />
                </div>

                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Event</label>
                    <input 
                      type="text"
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-2.5 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all outline-none" 
                      value={photoForm.event} 
                      onChange={e => setPhotoForm({ ...photoForm, event: e.target.value })}
                      placeholder="Optional event name"
                    />
                </div>

                <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                    <textarea 
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-2.5 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all outline-none resize-none"
                      rows="3"
                      value={photoForm.description} 
                      onChange={e => setPhotoForm({ ...photoForm, description: e.target.value })}
                      placeholder="Optional description"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end gap-4 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setPhotoForm(emptyPhoto);
                      setPhotoFile(null);
                      setError('');
                    }}
                    className="px-6 py-2.5 border-2 border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                  >
                    Clear
                  </button>
                  <button 
                    type="submit" 
                    disabled={saving || (!photoFile && !photoForm.url)}
                    className="inline-flex items-center gap-2 bg-primary-600 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
                  >
                    <Save className="h-4 w-4" /> 
                    {saving ? 'Saving...' : 'Add Photo'}
                  </button>
                </div>
              </form>
              )
            ) : (
              bulkMode ? (
                <form onSubmit={submitBulkVideos} className="space-y-6">
                  <div className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded-lg mb-4">
                    <p className="text-sm text-purple-700">
                      <strong>Bulk Upload Mode:</strong> Upload multiple video files or paste multiple video URLs (one per line). All videos will share the same category, event, and description below.
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Select Multiple Video Files
                      <span className="ml-2 text-xs text-amber-600 font-normal">(Coming Soon - Currently only URLs supported)</span>
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-400 transition-colors opacity-60">
                      <input
                        type="file"
                        accept="video/*"
                        multiple
                        onChange={handleBulkVideoFilesChange}
                        className="hidden"
                        id="bulk-video-upload"
                        disabled
                      />
                      <label
                        htmlFor="bulk-video-upload"
                        className="cursor-not-allowed flex flex-col items-center"
                      >
                        <Upload className="h-8 w-8 text-gray-400 mb-2" />
                        <span className="text-sm text-gray-600">
                          {videoFiles.length > 0 
                            ? `${videoFiles.length} file(s) selected (will be skipped)` 
                            : 'Video file uploads coming soon'}
                        </span>
                      </label>
                      {videoFiles.length > 0 && (
                        <div className="mt-4 text-left">
                          <p className="text-xs text-amber-600 mb-2 font-semibold">⚠️ Note: Video file uploads are not yet supported. These files will be skipped.</p>
                          <p className="text-xs text-gray-600 mb-2">Selected files:</p>
                          <div className="max-h-32 overflow-y-auto space-y-1">
                            {videoFiles.map((file, idx) => (
                              <div key={idx} className="text-xs text-gray-700 bg-white p-2 rounded border border-amber-200">
                                {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                              </div>
                            ))}
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setVideoFiles([]);
                              document.getElementById('bulk-video-upload').value = '';
                            }}
                            className="mt-2 text-xs text-red-600 hover:text-red-800"
                          >
                            Clear all files
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      <LinkIcon className="inline h-4 w-4 mr-1" />
                      Or Paste Video URLs (one per line)
                    </label>
                    <textarea 
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-2.5 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all outline-none resize-none font-mono text-sm"
                      rows="6"
                      value={videoUrls.join('\n')}
                      onChange={handleBulkVideoUrlsChange}
                      placeholder="https://youtube.com/watch?v=...
https://vimeo.com/...
https://example.com/video.mp4"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Enter one URL per line. Supports YouTube, Vimeo, or direct video file URLs. {videoUrls.length > 0 && `${videoUrls.length} URL(s) detected.`}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Category (applied to all)</label>
                      <input 
                        type="text"
                        className="w-full border-2 border-gray-300 rounded-lg px-4 py-2.5 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all outline-none" 
                        value={videoForm.category} 
                        onChange={e => setVideoForm({ ...videoForm, category: e.target.value })}
                        placeholder="e.g., Events, Training"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Event (applied to all)</label>
                      <input 
                        type="text"
                        className="w-full border-2 border-gray-300 rounded-lg px-4 py-2.5 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all outline-none" 
                        value={videoForm.event} 
                        onChange={e => setVideoForm({ ...videoForm, event: e.target.value })}
                        placeholder="Optional event name"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Description (applied to all)</label>
                      <textarea 
                        className="w-full border-2 border-gray-300 rounded-lg px-4 py-2.5 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all outline-none resize-none"
                        rows="3"
                        value={videoForm.description} 
                        onChange={e => setVideoForm({ ...videoForm, description: e.target.value })}
                        placeholder="Optional description"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-4 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => {
                        setVideoUrls([]);
                        setVideoFiles([]);
                        setVideoForm(emptyVideo);
                        setError('');
                        document.getElementById('bulk-video-upload').value = '';
                      }}
                      className="px-6 py-2.5 border-2 border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                    >
                      Clear
                    </button>
                    <button 
                      type="submit" 
                      disabled={bulkUploading || videoUrls.length === 0}
                      className="inline-flex items-center gap-2 bg-primary-600 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
                    >
                      <Save className="h-4 w-4" /> 
                      {bulkUploading ? `Uploading...` : `Add ${videoUrls.length} Video URL(s)${videoFiles.length > 0 ? ` (${videoFiles.length} file(s) will be skipped)` : ''}`}
                    </button>
                  </div>
                </form>
              ) : (
              <form onSubmit={submitVideo} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input 
                      type="text"
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-2.5 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all outline-none" 
                      value={videoForm.title} 
                      onChange={e => setVideoForm({ ...videoForm, title: e.target.value })}
                      placeholder="Enter video title"
                      required
                    />
                </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      <LinkIcon className="inline h-4 w-4 mr-1" />
                      Video URL <span className="text-red-500">*</span>
                    </label>
                    <input 
                      type="url"
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-2.5 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all outline-none" 
                      value={videoForm.url} 
                      onChange={e => setVideoForm({ ...videoForm, url: e.target.value })}
                      placeholder="https://youtube.com/watch?v=... or https://example.com/video.mp4"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Supports YouTube, Vimeo, or direct video file URLs</p>
                </div>

                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Category</label>
                    <input 
                      type="text"
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-2.5 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all outline-none" 
                      value={videoForm.category} 
                      onChange={e => setVideoForm({ ...videoForm, category: e.target.value })}
                      placeholder="e.g., Events, Training"
                    />
                </div>

                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Event</label>
                    <input 
                      type="text"
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-2.5 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all outline-none" 
                      value={videoForm.event} 
                      onChange={e => setVideoForm({ ...videoForm, event: e.target.value })}
                      placeholder="Optional event name"
                    />
                </div>

                <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                    <textarea 
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-2.5 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all outline-none resize-none"
                      rows="3"
                      value={videoForm.description} 
                      onChange={e => setVideoForm({ ...videoForm, description: e.target.value })}
                      placeholder="Optional description"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end gap-4 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setVideoForm(emptyVideo);
                      setVideoFile(null);
                      setError('');
                    }}
                    className="px-6 py-2.5 border-2 border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                  >
                    Clear
                  </button>
                  <button 
                    type="submit" 
                    disabled={saving}
                    className="inline-flex items-center gap-2 bg-primary-600 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
                  >
                    <Save className="h-4 w-4" /> 
                    {saving ? 'Saving...' : 'Add Video'}
                  </button>
                </div>
              </form>
              )
            )}
            </div>
          )}
        </div>

        {/* Gallery Items Display */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Photos List */}
          <div className="bg-white shadow-md rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-primary-50">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Image className="h-5 w-5 text-primary-600" />
                Photos ({photos.length})
              </h3>
            </div>
            <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center text-gray-500">Loading photos...</div>
              ) : photos.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Image className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No photos yet</p>
                  <p className="text-sm text-gray-400 mt-1">Add your first photo above</p>
                </div>
              ) : (
                photos.map(item => (
                  <div key={item._id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start gap-4">
                      {item.imageUrl && (
                        <div className="flex-shrink-0">
                          <img 
                            src={item.imageUrl && (item.imageUrl.startsWith('http') || item.imageUrl.startsWith('/') || item.imageUrl.startsWith('data:'))
                              ? item.imageUrl 
                              : item.imageUrl ? `${getBaseUrl().replace(/\/api\/?$/, '')}${item.imageUrl}` : 'https://via.placeholder.com/80?text=Image'}
                            alt={item.title}
                            className="w-20 h-20 object-cover rounded-lg border-2 border-gray-200"
                            onError={(e) => {
                              e.target.src = 'https://via.placeholder.com/80?text=Image';
                            }}
                          />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-900 mb-1">{item.title}</div>
                        <div className="text-xs text-gray-600 break-all mb-2">{item.imageUrl || item.url}</div>
                        {item.category && (
                          <span className="inline-block px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-md mr-2">
                            {item.category}
                          </span>
                        )}
                        {item.event && (
                          <span className="inline-block px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-md">
                            {item.event}
                          </span>
                        )}
                    </div>
                      <button 
                        onClick={() => handleDeletePhoto(item._id)} 
                        className="flex-shrink-0 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete photo"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Videos List */}
          <div className="bg-white shadow-md rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-pink-50">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <VideoIcon className="h-5 w-5 text-purple-600" />
                Videos ({videos.length})
              </h3>
            </div>
            <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center text-gray-500">Loading videos...</div>
              ) : videos.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <VideoIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No videos yet</p>
                  <p className="text-sm text-gray-400 mt-1">Add your first video above</p>
                </div>
              ) : (
                videos.map(item => (
                  <div key={item._id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 bg-purple-100 rounded-lg w-20 h-20 flex items-center justify-center">
                        <VideoIcon className="h-10 w-10 text-purple-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-900 mb-1">{item.title}</div>
                        <div className="text-xs text-gray-600 break-all mb-2">{item.videoUrl || item.url}</div>
                        {item.category && (
                          <span className="inline-block px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-md mr-2">
                            {item.category}
                          </span>
                        )}
                        {item.event && (
                          <span className="inline-block px-2 py-1 bg-pink-100 text-pink-700 text-xs rounded-md">
                            {item.event}
                          </span>
                        )}
                    </div>
                      <button 
                        onClick={() => handleDeleteVideo(item._id)} 
                        className="flex-shrink-0 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete video"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminGalleryPage;
